import { useState, useEffect } from 'react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface NotificationPrefs {
  emailEnabled: boolean;
  browserEnabled: boolean;
  oddsThreshold: number;
  emailAddress: string;
  notifyOddsDrops: boolean;
  notifyOddsRises: boolean;
  notifyLeagueUpdates: boolean;
  notifyPickResults: boolean;
}

export interface WatchedMatch {
  matchId: string;
  matchName: string;
  homeTeam: string;
  awayTeam: string;
  league: string;
  addedAt: string;
  lastOdds: { home: number; draw: number; away: number };
}

const DEFAULT_PREFS: NotificationPrefs = {
  emailEnabled: false,
  browserEnabled: false,
  oddsThreshold: 10,
  emailAddress: '',
  notifyOddsDrops: true,
  notifyOddsRises: false,
  notifyLeagueUpdates: true,
  notifyPickResults: false,
};

export const WATCHED_MATCHES_KEY = 'apuesta_mente_watched_matches';
export const NOTIF_PREFS_KEY = 'apuesta_mente_notif_prefs';

export function getNotifPrefs(): NotificationPrefs {
  try {
    const stored = localStorage.getItem(NOTIF_PREFS_KEY);
    if (stored) return { ...DEFAULT_PREFS, ...JSON.parse(stored) };
  } catch { /* ignore */ }
  return { ...DEFAULT_PREFS };
}

export function getWatchedMatches(): WatchedMatch[] {
  try {
    const stored = localStorage.getItem(WATCHED_MATCHES_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }
  return [];
}

export function saveWatchedMatches(matches: WatchedMatch[]) {
  localStorage.setItem(WATCHED_MATCHES_KEY, JSON.stringify(matches));
}

// ── Toggle component ──────────────────────────────────────────────────────────

function Toggle({ checked, onChange }: { checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer flex-shrink-0 ${
        checked ? 'bg-[#00e87a]' : 'bg-[#2a2d3a]'
      }`}
    >
      <span
        className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0'
        }`}
      ></span>
    </button>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface NotificationsModalProps {
  isOpen: boolean;
  onClose: () => void;
  userEmail?: string;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function NotificationsModal({ isOpen, onClose, userEmail = '' }: NotificationsModalProps) {
  const [prefs, setPrefs] = useState<NotificationPrefs>(() => {
    const p = getNotifPrefs();
    if (userEmail && !p.emailAddress) p.emailAddress = userEmail;
    return p;
  });
  const [watchedMatches, setWatchedMatches] = useState<WatchedMatch[]>([]);
  const [saved, setSaved] = useState(false);
  const [browserPermission, setBrowserPermission] = useState<'default' | 'granted' | 'denied'>('default');

  useEffect(() => {
    if ('Notification' in window) {
      setBrowserPermission(Notification.permission as 'default' | 'granted' | 'denied');
    }
    setWatchedMatches(getWatchedMatches());
  }, [isOpen]);

  useEffect(() => {
    if (userEmail && !prefs.emailAddress) {
      setPrefs((p) => ({ ...p, emailAddress: userEmail }));
    }
  }, [userEmail]);

  const handleBrowserToggle = async (val: boolean) => {
    if (!val) { setPrefs((p) => ({ ...p, browserEnabled: false })); return; }
    if (!('Notification' in window)) return;
    const perm = await Notification.requestPermission();
    setBrowserPermission(perm as 'default' | 'granted' | 'denied');
    if (perm === 'granted') {
      setPrefs((p) => ({ ...p, browserEnabled: true }));
      new Notification('¡Notificaciones activadas!', {
        body: 'Te avisaremos cuando cambien las cuotas de tus partidos.',
        icon: '/favicon.ico',
      });
    } else {
      setPrefs((p) => ({ ...p, browserEnabled: false }));
    }
  };

  const handleRemoveMatch = (matchId: string) => {
    const updated = watchedMatches.filter((m) => m.matchId !== matchId);
    setWatchedMatches(updated);
    saveWatchedMatches(updated);
  };

  const handleSave = () => {
    localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(prefs));
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  const setPref = <K extends keyof NotificationPrefs>(k: K, v: NotificationPrefs[K]) =>
    setPrefs((p) => ({ ...p, [k]: v }));

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-[#151719] border border-[#2a2d3a] rounded-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2a2d3a] shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-[#ffd700]/15 text-[#ffd700]">
              <i className="ri-notification-3-line text-lg"></i>
            </div>
            <div>
              <h2 className="text-white font-bold text-base">Configurar Alertas</h2>
              <p className="text-[#6b7280] text-xs">Notificaciones de cuotas en tiempo real</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-[#6b7280] hover:text-white hover:bg-white/10 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-4">

          {/* Browser notifications */}
          <div className="bg-[#0d0f14] rounded-xl p-4 border border-[#2a2d3a]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#00e87a]/15 text-[#00e87a]">
                  <i className="ri-notification-4-line text-sm"></i>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">Notificaciones del Navegador</p>
                  <p className="text-[#6b7280] text-xs">Alertas push instantáneas</p>
                </div>
              </div>
              <Toggle checked={prefs.browserEnabled} onChange={handleBrowserToggle} />
            </div>
            {browserPermission === 'denied' && (
              <div className="flex items-start gap-2 mt-2 px-3 py-2 bg-[#ef4444]/10 border border-[#ef4444]/20 rounded-lg">
                <div className="w-4 h-4 flex items-center justify-center text-[#ef4444] mt-0.5 flex-shrink-0">
                  <i className="ri-error-warning-line text-xs"></i>
                </div>
                <p className="text-[#ef4444] text-xs">Bloqueadas en el navegador. Actívalas en Configuración del sitio.</p>
              </div>
            )}
            {prefs.browserEnabled && browserPermission === 'granted' && (
              <div className="flex items-center gap-2 mt-2 px-3 py-2 bg-[#00e87a]/10 rounded-lg">
                <div className="w-4 h-4 flex items-center justify-center text-[#00e87a] flex-shrink-0">
                  <i className="ri-check-line text-xs"></i>
                </div>
                <p className="text-[#00e87a] text-xs font-medium">Notificaciones push activas</p>
              </div>
            )}
          </div>

          {/* Email notifications */}
          <div className="bg-[#0d0f14] rounded-xl p-4 border border-[#2a2d3a]">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#ffd700]/15 text-[#ffd700]">
                  <i className="ri-mail-line text-sm"></i>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">Alertas por Email</p>
                  <p className="text-[#6b7280] text-xs">Email automático cuando cambie una cuota</p>
                </div>
              </div>
              <Toggle checked={prefs.emailEnabled} onChange={(v) => setPref('emailEnabled', v)} />
            </div>
            {prefs.emailEnabled && (
              <div className="mt-3 space-y-2">
                <input
                  type="email"
                  value={prefs.emailAddress}
                  onChange={(e) => setPref('emailAddress', e.target.value)}
                  placeholder="tu@email.com"
                  className="w-full bg-[#151719] border border-[#2a2d3a] text-white text-sm rounded-xl px-4 py-2.5 placeholder-[#6b7280] focus:outline-none focus:border-[#ffd700]/40"
                />
                <div className="flex items-start gap-2 px-3 py-2 bg-[#ffd700]/5 border border-[#ffd700]/20 rounded-lg">
                  <div className="w-4 h-4 flex items-center justify-center text-[#ffd700] mt-0.5 flex-shrink-0">
                    <i className="ri-information-line text-xs"></i>
                  </div>
                  <p className="text-[#a8b2c1] text-xs leading-relaxed">
                    Los emails se envían automáticamente cuando se detecta un cambio superior al umbral configurado en partidos que sigues.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Odds drop/rise threshold */}
          <div className="bg-[#0d0f14] rounded-xl p-4 border border-[#2a2d3a]">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#ff6b35]/15 text-[#ff6b35]">
                <i className="ri-percent-line text-sm"></i>
              </div>
              <div>
                <p className="text-white text-sm font-semibold">Umbral de cambio de cuota</p>
                <p className="text-[#6b7280] text-xs">Alertar cuando una cuota cambie más del:</p>
              </div>
            </div>
            <div className="flex gap-2 flex-wrap mb-3">
              {[5, 10, 15, 20, 25].map((pct) => (
                <button
                  key={pct}
                  onClick={() => setPref('oddsThreshold', pct)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer whitespace-nowrap transition-all ${
                    prefs.oddsThreshold === pct
                      ? 'bg-[#ff6b35] text-black'
                      : 'bg-[#151719] border border-[#2a2d3a] text-[#6b7280] hover:text-white'
                  }`}
                >
                  {pct}%
                </button>
              ))}
            </div>

            {/* Notify on drops / rises */}
            <div className="space-y-2.5 pt-2 border-t border-[#1e2028]">
              <p className="text-[#6b7280] text-xs font-semibold uppercase tracking-wide pt-1">Dirección de la alerta</p>
              {[
                { key: 'notifyOddsDrops' as const, label: 'Caídas de cuota', icon: 'ri-arrow-down-circle-line', color: '#ef4444' },
                { key: 'notifyOddsRises' as const, label: 'Subidas de cuota', icon: 'ri-arrow-up-circle-line', color: '#00e87a' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 flex items-center justify-center" style={{ color: item.color }}>
                      <i className={`${item.icon} text-sm`}></i>
                    </div>
                    <p className="text-white text-sm">{item.label}</p>
                  </div>
                  <Toggle checked={prefs[item.key] as boolean} onChange={(v) => setPref(item.key, v)} />
                </div>
              ))}
            </div>
          </div>

          {/* Other notification types */}
          <div className="bg-[#0d0f14] rounded-xl p-4 border border-[#2a2d3a]">
            <p className="text-[#6b7280] text-xs font-bold uppercase tracking-wide mb-3">Otros tipos de alerta</p>
            <div className="space-y-3">
              {[
                { key: 'notifyLeagueUpdates' as const, label: 'Actualizaciones de liga', desc: 'Cambios de posición, nuevos participantes', icon: 'ri-trophy-line', color: '#ffd700' },
                { key: 'notifyPickResults' as const, label: 'Resultados de picks', desc: 'Cuando un pick tuyo sea verificado', icon: 'ri-check-double-line', color: '#00e87a' },
              ].map((item) => (
                <div key={item.key} className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0" style={{ background: `${item.color}15`, color: item.color }}>
                      <i className={`${item.icon} text-sm`}></i>
                    </div>
                    <div className="min-w-0">
                      <p className="text-white text-sm font-medium">{item.label}</p>
                      <p className="text-[#3d4149] text-xs truncate">{item.desc}</p>
                    </div>
                  </div>
                  <Toggle checked={prefs[item.key] as boolean} onChange={(v) => setPref(item.key, v)} />
                </div>
              ))}
            </div>
          </div>

          {/* Watched matches */}
          <div className="bg-[#0d0f14] rounded-xl p-4 border border-[#2a2d3a]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#ffd700]/15 text-[#ffd700]">
                  <i className="ri-star-line text-sm"></i>
                </div>
                <div>
                  <p className="text-white text-sm font-semibold">Partidos seguidos</p>
                  <p className="text-[#6b7280] text-xs">Recibirás alertas cuando cambien sus cuotas</p>
                </div>
              </div>
              {watchedMatches.length > 0 && (
                <span className="px-2 py-0.5 rounded-full bg-[#ffd700]/20 text-[#ffd700] text-xs font-black">
                  {watchedMatches.length}
                </span>
              )}
            </div>

            {watchedMatches.length === 0 ? (
              <div className="flex items-start gap-3 px-4 py-3 bg-[#151719] border border-dashed border-[#2a2d3a] rounded-xl">
                <div className="w-4 h-4 flex items-center justify-center text-[#6b7280] mt-0.5 flex-shrink-0">
                  <i className="ri-information-line text-sm"></i>
                </div>
                <div>
                  <p className="text-[#a8b2c1] text-xs leading-relaxed">
                    Aún no sigues ningún partido. Ve al{' '}
                    <a href="/comparador" className="text-[#ffd700] font-medium cursor-pointer" onClick={onClose}>
                      Comparador de Cuotas
                    </a>
                    {' '}y pulsa el icono{' '}
                    <i className="ri-star-line text-[#ffd700] text-xs"></i>
                    {' '}en cualquier partido para empezar a seguirlo.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                {watchedMatches.map((match) => (
                  <div key={match.matchId} className="flex items-center gap-3 px-3 py-3 bg-[#151719] border border-[#1e2028] rounded-xl">
                    <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-[#ffd700]/10 flex-shrink-0">
                      <i className="ri-star-fill text-[#ffd700] text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-xs font-semibold truncate">
                        {match.homeTeam} vs {match.awayTeam}
                      </p>
                      <div className="flex items-center gap-3 mt-0.5">
                        <p className="text-[#6b7280] text-xs truncate">{match.league}</p>
                        <div className="flex items-center gap-1.5 text-[#3d4149] text-xs">
                          <span>1: <strong className="text-[#a8b2c1]">{match.lastOdds.home.toFixed(2)}</strong></span>
                          {match.lastOdds.draw > 0 && (
                            <span>X: <strong className="text-[#a8b2c1]">{match.lastOdds.draw.toFixed(2)}</strong></span>
                          )}
                          <span>2: <strong className="text-[#a8b2c1]">{match.lastOdds.away.toFixed(2)}</strong></span>
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={() => handleRemoveMatch(match.matchId)}
                      className="w-7 h-7 flex items-center justify-center text-[#6b7280] hover:text-[#ef4444] hover:bg-[#ef4444]/10 rounded-lg transition-all cursor-pointer flex-shrink-0"
                    >
                      <i className="ri-close-line text-sm"></i>
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => { saveWatchedMatches([]); setWatchedMatches([]); }}
                  className="w-full py-2 text-xs text-[#6b7280] hover:text-[#ef4444] cursor-pointer transition-colors"
                >
                  Dejar de seguir todos
                </button>
              </div>
            )}
          </div>

          {/* Setup note for email */}
          {prefs.emailEnabled && (
            <div className="flex items-start gap-3 px-4 py-3 bg-[#ff6b35]/5 border border-[#ff6b35]/20 rounded-xl">
              <div className="w-4 h-4 flex items-center justify-center text-[#ff6b35] mt-0.5 flex-shrink-0">
                <i className="ri-settings-3-line text-sm"></i>
              </div>
              <p className="text-[#a8b2c1] text-xs leading-relaxed">
                Para que lleguen los emails, el administrador debe añadir{' '}
                <code className="text-[#ff6b35] bg-[#0d0f14] px-1 py-0.5 rounded font-mono">RESEND_API_KEY</code>
                {' '}en Supabase → Edge Functions → Secrets, y verificar el dominio en{' '}
                <strong className="text-white">resend.com</strong>.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[#2a2d3a] shrink-0">
          {saved ? (
            <div className="flex items-center justify-center gap-2 py-3 bg-[#00e87a]/15 rounded-xl border border-[#00e87a]/30">
              <div className="w-4 h-4 flex items-center justify-center text-[#00e87a]">
                <i className="ri-check-double-line text-sm"></i>
              </div>
              <span className="text-[#00e87a] text-sm font-bold">¡Preferencias guardadas!</span>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 border border-[#2a2d3a] text-[#a8b2c1] text-sm font-medium rounded-xl hover:bg-white/5 cursor-pointer whitespace-nowrap transition-all"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                className="flex-1 py-3 bg-[#ffd700] text-black text-sm font-bold rounded-xl hover:bg-[#f0c800] cursor-pointer whitespace-nowrap transition-all flex items-center justify-center gap-2"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-save-line text-sm"></i>
                </div>
                Guardar Preferencias
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
